/**
 * useShellModuleAccess — assembles the shell's single, fail-closed entitlement
 * gate and owns the batch evaluation that feeds it.
 *
 * Extracted from AppShell so the authorization wiring lives in one readable
 * place (and so the shell host stays under the view-LOC ceiling). The pure
 * decision logic is in `entitlementGate.ts`; this hook only supplies its
 * inputs:
 *
 *   • the manifest catalogue (the widest possible set),
 *   • the nav-mode workspace filter (presentation narrowing, never widening),
 *   • the evaluator's phase + allowed set,
 *   • the documented `dev-token-*` development wildcard allowance.
 *
 * THE ENTITLEMENT SOURCE
 * ----------------------
 * The batch reads the CALLER-SCOPED contract
 * (`POST /auth/entitlements/evaluate`, see `SELF_ENTITLEMENT_PATH`) through the
 * shared API client, which prepends `/api/v1` and attaches the session bearer +
 * tenant header. The server derives roles / edition / feature flags from the
 * authenticated principal and tenant, so nothing about the principal is sent
 * from here — there is no client-side principal to forge, and the whole
 * catalogue is answered in ONE round trip.
 *
 * The previous source was the ADMIN rules-preview route
 * (`/admin/entitlement-rules/evaluate`), which exists to preview a rule against
 * a SYNTHETIC principal and is gated by `Admin.Full`. Under correct fail-closed
 * semantics that meant an ordinary operator got 403 → indeterminate → an empty
 * catalogue. The gate was right; the contract was the missing piece.
 *
 * Failure handling is unchanged and deliberate. A transport error, a 401/403, a
 * 5xx, an unreadable body, or an unanswered target all mark the batch
 * `unavailable`, and `resolveModuleAccess` then permits NOTHING — there is no
 * path from a failure to an allow. The returned `retry` bumps a token
 * that re-runs the batch, so the "could not verify your access" state is
 * recoverable without a page reload.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient, useAuth } from '@tyndo/hooks';
import { moduleManifestRegistry } from '../registries';
import { useNavMode, resolveModeVisibleModules } from './NavModeContext';
import {
  fetchSelfEntitlements,
  isWildcardDevSession,
  resolveModuleAccess,
  SELF_ENTITLEMENT_PATH,
  type EntitlementPhase,
  type ModuleAccess,
} from './entitlementGate';

/** Narrow an `unknown` to an indexable object before reading a property off it. */
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

export interface ShellModuleAccess {
  access: ModuleAccess;
  /** Re-run the entitlement batch (the "Try again" affordance). */
  retry: () => void;
}

export function useShellModuleAccess(options: {
  /**
   * Number of roles on the current principal. A change re-evaluates, because
   * entitlement rules are role-conditioned.
   */
  roleCount: number;
  /**
   * A value that moves whenever the module registries mutate (Pass 2 federated
   * registration), so the catalogue snapshot is recomputed on exactly those
   * beats rather than on every render.
   */
  registryRevision: unknown;
}): ShellModuleAccess {
  const { roleCount, registryRevision } = options;
  const { mode: navMode, persona: navPersona, industry: navIndustry } = useNavMode();

  // The development wildcard allowance: a `dev-token-*` bearer is the
  // gateway's documented local bypass (mirrored by PermissionsProvider's
  // wildcard short-circuit). Such a session skips evaluation entirely —
  // nothing is evaluated, so no failure can be reinterpreted as an allow.
  const { accessToken } = useAuth();
  const wildcardSession = isWildcardDevSession(accessToken);

  const apiClient = useApiClient();

  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => setRetryToken((n) => n + 1), []);

  const [entitlementPhase, setEntitlementPhase] = useState<EntitlementPhase>('idle');
  const [entitledModuleIds, setEntitledModuleIds] = useState<ReadonlySet<string> | null>(null);

  // ── The catalogue snapshot, stabilised by CONTENT ─────────────────────────
  //
  // `registryRevision` is only a hint that the registries MAY have moved. The
  // shell passes the role-filtered menu tree, and `MenuRegistry.getVisibleMenus`
  // rebuilds that array (and every node in it) on every call — so its identity
  // changes on EVERY render of the host. Depending on it directly made the
  // batch effect re-run each render: the effect set `loading`, the answer set
  // `ready`, the resulting render produced a new revision identity, and the
  // effect fired again — an unbounded request loop that never left the loading
  // state, because the gate only reports `ready` between two renders that are
  // immediately superseded.
  //
  // The fix is to depend on the QUESTION rather than on the object that hinted
  // at it: recompute the snapshot whenever the revision moves (cheap — one
  // registry list), but let only a change in the ids reach the effect and the
  // memos below. Registry mutations still re-evaluate, because a mutation
  // changes the ids; a re-render alone no longer does.
  const snapshot = useMemo(() => {
    const manifestEntries = moduleManifestRegistry.list?.() ?? [];
    const moduleIds = manifestEntries
      .map((m) => m?.moduleId)
      .filter((x): x is string => Boolean(x));
    const widgetIds = manifestEntries
      .flatMap((m) => (Array.isArray(m?.widgets) ? m.widgets : []))
      .map((w) => (isRecord(w) ? w.widgetId : undefined))
      .filter((x): x is string => typeof x === 'string');
    return { moduleIds, widgetIds };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryRevision]);

  /** Content signature of the snapshot — two equal signatures are one question. */
  const catalogueKey = `${snapshot.moduleIds.join('|')}::${snapshot.widgetIds.join('|')}`;

  /** The snapshot every consumer below sees: new identity only on new content. */
  const catalogue = useMemo(
    () => snapshot,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogueKey],
  );

  // Monotonic batch id. A slower earlier batch must never overwrite the result
  // of a later one (roles changed, tenant switched, manual retry), otherwise a
  // stale response could reinstate a superseded permission set.
  const batchSeqRef = useRef(0);

  // Kick off a one-shot entitlement evaluation for every module + widget the
  // manifest declares — one request for the whole catalogue.
  useEffect(() => {
    if (wildcardSession) {
      // Nothing is evaluated, so nothing can have failed: stay `idle` and let
      // the gate's wildcard branch (which runs AFTER its failure branch)
      // interpret it.
      batchSeqRef.current += 1;
      setEntitlementPhase('idle');
      setEntitledModuleIds(null);
      return;
    }

    const { moduleIds, widgetIds } = catalogue;

    const seq = ++batchSeqRef.current;
    // Until the batch resolves the shell renders an explicit loading state and
    // permits NOTHING.
    setEntitlementPhase('loading');
    setEntitledModuleIds(null);

    void (async () => {
      const result = await fetchSelfEntitlements(
        (body) => apiClient.post(SELF_ENTITLEMENT_PATH, body),
        { moduleIds, widgetIds },
      );
      // A newer batch superseded this one while it was in flight — drop the
      // stale result rather than clobbering the current permission set.
      if (batchSeqRef.current !== seq) return;
      if (result.phase === 'unavailable') {
        setEntitlementPhase('unavailable');
        setEntitledModuleIds(null);
        return;
      }
      setEntitledModuleIds(result.modules);
      setEntitlementPhase('ready');
    })();
    // `catalogue` — not `registryRevision` — is the dependency: it moves when
    // the question changes and stands still when only the host re-rendered.
  }, [apiClient, wildcardSession, roleCount, catalogue, retryToken]);

  // Every module the manifest registered — the widest possible catalogue, and
  // the only thing the gate is allowed to narrow from. Same snapshot the batch
  // asked about, so the gate can never be handed a catalogue the evaluator was
  // not asked about.
  const catalogueModuleIds = catalogue.moduleIds;

  // Nav-mode (persona / industry) presentation filter. Never an authorization
  // input on its own. It is resolved AGAINST the catalogue snapshot because the
  // persona / industry manifests name bare modules (`quality`) while the
  // catalogue is provider-namespaced (`tyndo.quality`) — handing the gate the
  // manifest's own names intersects to nothing.
  const modeVisibleModuleIds = useMemo(
    () => resolveModeVisibleModules(navMode, navPersona, navIndustry, catalogueModuleIds),
    [navMode, navPersona, navIndustry, catalogueModuleIds],
  );

  const access = useMemo(
    () =>
      resolveModuleAccess({
        phase: entitlementPhase,
        entitledModuleIds,
        modeVisibleModuleIds,
        catalogueModuleIds,
        wildcardSession,
      }),
    [
      entitlementPhase,
      entitledModuleIds,
      modeVisibleModuleIds,
      catalogueModuleIds,
      wildcardSession,
    ],
  );

  return { access, retry };
}
